// src/pages/PricingStatic.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, X, Crown, Star, User, Building, Loader2 } from 'lucide-react';
import { useTranslation, useLanguage } from '../contexts/LanguageContext';
import NavBar from '../components/Navbar/NavBar';
import Footer from '../components/Footer';

const Pricing = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { language: contextLanguage } = useLanguage();

  // Language state
  const [language, setLanguage] = useState('en');
  const [userType, setUserType] = useState('individual');
  const [billingInterval, setBillingInterval] = useState('monthly');
  const [plansLoading, setPlansLoading] = useState(false); // No loading needed for static data

  // Initialize language from localStorage
  useEffect(() => {
    const savedLanguage = localStorage.getItem('easysubs-language');
    if (savedLanguage && (savedLanguage === 'en' || savedLanguage === 'it')) {
      setLanguage(savedLanguage);
    } else {
      setLanguage('en');
      localStorage.setItem('easysubs-language', 'en');
    }
  }, []);

  // Sync with context language
  useEffect(() => {
    if (contextLanguage && (contextLanguage === 'en' || contextLanguage === 'it')) {
      setLanguage(contextLanguage);
    }
  }, [contextLanguage]);

  // Hardcoded plans data (8 documents)
  const allPlans = [
    // English - Individual Free
    {
      planId: "individual_free_en",
      name: "Free Plan",
      description: "Start managing your subscriptions with basic features",
      language: "en",
      userType: "individual",
      tier: "free",
      pricing: {
        monthly: { amount: 0, currency: "EUR" }
      },
      features: [
        { name: "Active subscription tracking", included: true },
        { name: "Price change alerts", included: true },
        { name: "Free trial alerts (up to 3)", included: true },
        { name: "Sharing spaces (up to 3)", included: true },
        { name: "Promotion alerts (up to 5)", included: true },
        { name: "Partner exclusive offers", included: true },
        { name: "Access via Web, iOS & Android", included: true },
        { name: "Merchant comparison", included: true }
      ],
      metadata: { recommended: false, popular: false }
    },
    // English - Individual Premium
    {
      planId: "individual_premium_en",
      name: "Premium Plan",
      description: "Advanced tools for managing personal subscriptions",
      language: "en",
      userType: "individual",
      tier: "premium",
      pricing: {
        monthly: { amount: 3.49, currency: "EUR" },
        yearly: { amount: 34.9, currency: "EUR", discount: "17%" }
      },
      features: [
        { name: "Active subscription tracking", included: true },
        { name: "Price change alerts", included: true },
        { name: "Unlimited trial alerts", included: true },
        { name: "Unlimited sharing spaces", included: true },
        { name: "Unlimited promotion alerts", included: true },
        { name: "Exclusive partner offers", included: true },
        { name: "Multi-device access", included: true },
        { name: "Merchant comparisons", included: true },
        { name: "Advanced analytics & yearly reports", included: true }
      ],
      metadata: { recommended: true, popular: true }
    },
    // English - Company Free
    {
      planId: "company_free_en",
      name: "Business Free",
      description: "Basic subscription management for companies",
      language: "en",
      userType: "company",
      tier: "free",
      pricing: {
        monthly: { amount: 0, currency: "EUR" }
      },
      features: [
        { name: "Active subscription tracking", included: true },
        { name: "Price change alerts", included: true },
        { name: "Free trial alerts (up to 3)", included: true },
        { name: "Sharing spaces (up to 3)", included: true },
        { name: "Promotion alerts (up to 5)", included: true },
        { name: "Partner benefits", included: true },
        { name: "Multi-device access", included: true },
        { name: "Merchant comparison", included: true }
      ],
      metadata: { recommended: false, popular: false }
    },
    // English - Company Premium
    {
      planId: "company_premium_en",
      name: "Business Premium",
      description: "Professional subscription management tools",
      language: "en",
      userType: "company",
      tier: "premium",
      pricing: {
        monthly: { amount: 9.99, currency: "EUR" },
        yearly: { amount: 99.99, currency: "EUR", discount: "17%" }
      },
      features: [
        { name: "Active subscription tracking", included: true },
        { name: "Price change alerts", included: true },
        { name: "Unlimited trial alerts", included: true },
        { name: "Unlimited sharing spaces", included: true },
        { name: "Unlimited promotion alerts", included: true },
        { name: "Partner advantages", included: true },
        { name: "Access across devices", included: true },
        { name: "Advanced comparison tools", included: true },
        { name: "Yearly reports and analytics", included: true }
      ],
      metadata: { recommended: true, popular: false }
    },
    // Italian - Individual Free
    {
      planId: "individual_free_it",
      name: "Piano Gratuito",
      description: "Inizia a gestire i tuoi abbonamenti con le funzionalità base",
      language: "it",
      userType: "individual",
      tier: "free",
      pricing: {
        monthly: { amount: 0, currency: "EUR" }
      },
      features: [
        { name: "Monitoraggio abbonamenti", included: true },
        { name: "Avvisi cambio prezzo", included: true },
        { name: "Avvisi prova gratuita (fino a 3)", included: true },
        { name: "Spazi di condivisione (fino a 3)", included: true },
        { name: "Avvisi promozioni (fino a 5)", included: true },
        { name: "Offerte partner", included: true },
        { name: "Accesso multi-dispositivo", included: true },
        { name: "Confronto servizi", included: true }
      ],
      metadata: { recommended: false, popular: false }
    },
    // Italian - Individual Premium
    {
      planId: "individual_premium_it",
      name: "Piano Premium",
      description: "Strumenti avanzati per gestire gli abbonamenti",
      language: "it",
      userType: "individual",
      tier: "premium",
      pricing: {
        monthly: { amount: 3.49, currency: "EUR" },
        yearly: { amount: 34.9, currency: "EUR", discount: "17%" }
      },
      features: [
        { name: "Monitoraggio abbonamenti", included: true },
        { name: "Avvisi variazioni prezzo", included: true },
        { name: "Avvisi prove illimitati", included: true },
        { name: "Condivisione illimitata", included: true },
        { name: "Avvisi promozioni illimitati", included: true },
        { name: "Offerte partner esclusive", included: true },
        { name: "Accesso totale", included: true },
        { name: "Confronto avanzato", included: true },
        { name: "Report & analisi annuali", included: true }
      ],
      metadata: { recommended: true, popular: true }
    },
    // Italian - Company Free
    {
      planId: "company_free_it",
      name: "Business Gratuito",
      description: "Gestione semplificata degli abbonamenti aziendali",
      language: "it",
      userType: "company",
      tier: "free",
      pricing: {
        monthly: { amount: 0, currency: "EUR" }
      },
      features: [
        { name: "Monitoraggio aziendale", included: true },
        { name: "Avvisi prezzo", included: true },
        { name: "Avvisi prova (fino a 3)", included: true },
        { name: "Spazi condivisi (fino a 3)", included: true },
        { name: "Avvisi promozioni (fino a 5)", included: true },
        { name: "Offerte partner", included: true },
        { name: "Accesso aziendale", included: true },
        { name: "Confronto fornitori", included: true }
      ],
      metadata: { recommended: false, popular: false }
    },
    // Italian - Company Premium
    {
      planId: "company_premium_it",
      name: "Business Premium",
      description: "Gestione professionale degli abbonamenti aziendali",
      language: "it",
      userType: "company",
      tier: "premium",
      pricing: {
        monthly: { amount: 9.99, currency: "EUR" },
        yearly: { amount: 99.99, currency: "EUR", discount: "17%" }
      },
      features: [
        { name: "Controllo totale", included: true },
        { name: "Avvisi automatici", included: true },
        { name: "Prove illimitate", included: true },
        { name: "Spazi illimitati", included: true },
        { name: "Offerte senza limiti", included: true },
        { name: "Vantaggi partner", included: true },
        { name: "Accesso completo", included: true },
        { name: "Analisi evoluta", included: true },
        { name: "Report aziendali", included: true }
      ],
      metadata: { recommended: true, popular: false }
    }
  ];

  // Filter plans by current language and userType
  const filteredPlans = allPlans.filter(
    plan => plan.language === language && plan.userType === userType
  );

  const getPlanByTier = (tier) => filteredPlans.find(plan => plan.tier === tier);

  const freePlan = getPlanByTier('free');
  const premiumPlan = getPlanByTier('premium');

  if (plansLoading) {
    return (
      <>
        <NavBar />
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#253985] mx-auto"></div>
            <p className="mt-4 text-gray-600">{t('common.loading')}</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <NavBar />
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <Crown className="h-16 w-16 mx-auto text-yellow-500 mb-4" />
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              {t('pricing.title') || 'Simple, transparent pricing'}
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {t('pricing.subtitle') || 'Choose the plan that fits your needs. Start for free, upgrade anytime.'}
            </p>
          </div>

          {/* User Type Toggle */}
          <div className="flex justify-center mb-8">
            <div className="bg-gray-100 p-1 rounded-lg inline-flex">
              <button
                className={`px-4 py-2 rounded-md flex items-center transition-colors ${
                  userType === 'individual' 
                    ? 'bg-white text-[#253985] shadow-sm' 
                    : 'bg-transparent text-gray-600'
                }`}
                onClick={() => setUserType('individual')}
              >
                <User className="h-4 w-4 mr-2" />
                {t('pricing.personal') || 'Personal'}
              </button>
              <button
                className={`px-4 py-2 rounded-md flex items-center transition-colors ${
                  userType === 'company' 
                    ? 'bg-white text-[#253985] shadow-sm' 
                    : 'bg-transparent text-gray-600'
                }`}
                onClick={() => setUserType('company')}
              >
                <Building className="h-4 w-4 mr-2" />
                {t('pricing.business') || 'Business'}
              </button>
            </div>
          </div>

          {/* No Plans Found */}
          {filteredPlans.length === 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6 max-w-2xl mx-auto">
              <p className="text-yellow-700">
                {t('pricing.noPlans') || 'No plans available for the selected account type.'}
              </p>
            </div>
          )}

          {/* Plans Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Free Plan */}
            {freePlan && (
              <div className="border border-gray-200 rounded-2xl p-6 bg-white hover:shadow-md transition-all">
                <div className="flex items-center mb-4">
                  <div className="bg-blue-100 p-2 rounded-lg">
                    <Star className="h-6 w-6 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-semibold ml-3">{freePlan.name}</h3>
                </div>

                <div className="mb-6">
                  <span className="text-3xl font-bold">€{freePlan.pricing.monthly.amount}</span>
                  <span className="text-gray-600">{t('common.perMonth')}</span>
                </div>

                <ul className="space-y-3 mb-6">
                  {freePlan.features.map((feature, index) => (
                    <li key={index} className="flex items-center">
                      <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                      <span className="text-gray-700">{feature.name}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => navigate('/demo')}
                  className="w-full py-3 rounded-lg font-medium bg-gray-100 text-gray-800 hover:bg-gray-200 transition-colors"
                >
                  {t('pricing.signUpFree') || 'Sign up for free'}
                </button>
              </div>
            )}

            {/* Premium Plan */}
            {premiumPlan && (
              <div className="border-2 border-[#253985] rounded-2xl p-6 bg-white shadow-lg relative">
                {premiumPlan.metadata.recommended && (
                  <div className="absolute top-0 right-0 bg-yellow-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg rounded-tr-lg">
                    {t('pricing.recommended') || 'Recommended'}
                  </div>
                )}

                <div className="flex items-center mb-4">
                  <div className="bg-purple-100 p-2 rounded-lg">
                    <Crown className="h-6 w-6 text-purple-600" />
                  </div>
                  <h3 className="text-xl font-semibold ml-3">{premiumPlan.name}</h3>
                </div>

                {/* Price Display */}
                <div className="mb-4">
                  {billingInterval === 'monthly' ? (
                    <>
                      <span className="text-3xl font-bold">€{premiumPlan.pricing.monthly.amount}</span>
                      <span className="text-gray-600">{t('common.perMonth')}</span>
                      {premiumPlan.pricing.yearly && (
                        <p className="text-sm text-gray-500 mt-1">
                          {t('pricing.orYearly') || 'or'} €{premiumPlan.pricing.yearly.amount}{t('pricing.perYear') || '/year'} 
                          {(() => {
                            const monthly = premiumPlan.pricing.monthly.amount;
                            const yearly = premiumPlan.pricing.yearly.amount;
                            const discount = ((monthly * 12 - yearly) / (monthly * 12) * 100).toFixed(0);
                            return discount > 0 ? ` (save ${discount}%)` : '';
                          })()}
                        </p>
                      )}
                    </>
                  ) : (
                    <>
                      <span className="text-3xl font-bold">€{premiumPlan.pricing.yearly.amount}</span>
                      <span className="text-gray-600">{t('pricing.perYear') || '/year'}</span>
                      <p className="text-sm text-gray-500 mt-1">
                        {t('pricing.just') || 'just'} €{(premiumPlan.pricing.yearly.amount / 12).toFixed(2)}
                        {t('common.perMonth')} {t('pricing.whenBilledAnnually') || 'when billed annually'}
                      </p>
                    </>
                  )}
                </div>

                {/* Billing Interval Toggle */}
                {premiumPlan.pricing.yearly && (
                  <div className="relative mb-4">
                    {/* Discount badge */}
                    {(() => {
                      const monthly = premiumPlan.pricing.monthly.amount;
                      const yearly = premiumPlan.pricing.yearly.amount;
                      const discount = ((monthly * 12 - yearly) / (monthly * 12) * 100).toFixed(0);
                      if (discount > 0) {
                        return (
                          <div className="absolute -top-2 right-0 left-1/2 transform -translate-x-1/2 w-max z-10">
                            <span className="bg-green-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-sm">
                              {t('pricing.save') || 'Save'} {discount}%
                            </span>
                          </div>
                        );
                      }
                      return null;
                    })()}

                    <div className="flex bg-gray-100 rounded-lg p-1 relative">
                      <button
                        className={`flex-1 py-2 rounded-md text-sm font-medium transition-colors ${
                          billingInterval === 'monthly' ? 'bg-white text-[#253985] shadow-sm' : 'text-gray-600'
                        }`}
                        onClick={() => setBillingInterval('monthly')}
                      >
                        {t('pricing.monthly') || 'Monthly'} · €{premiumPlan.pricing.monthly.amount}/mo
                      </button>
                      <button
                        className={`flex-1 py-2 rounded-md text-sm font-medium transition-colors ${
                          billingInterval === 'yearly' ? 'bg-white text-[#253985] shadow-sm' : 'text-gray-600'
                        }`}
                        onClick={() => setBillingInterval('yearly')}
                      >
                        {t('pricing.yearly') || 'Yearly'} · €{premiumPlan.pricing.yearly.amount}/year
                      </button>
                    </div>
                  </div>
                )}

                <div className="mb-6">
                  <h4 className="font-semibold text-gray-800 mb-2">
                    {t('pricing.allFreeFeaturesPlus') || 'All Free features, plus:'}
                  </h4>
                  <ul className="space-y-3">
                    {premiumPlan.features.map((feature, index) => (
                      <li key={index} className="flex items-center">
                        <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                        <span className="text-gray-700">{feature.name}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <button
                  onClick={() => navigate('/demo')}
                  className="w-full py-3 rounded-lg font-medium bg-[#253985] text-white hover:bg-[#1a2c6d] transition-colors flex items-center justify-center"
                >
                  {t('pricing.getStarted') || 'Get started'}
                </button>
              </div>
            )}
          </div>

          {/* Note about account type change */}
          <div className="mt-12 bg-blue-50 border border-blue-200 rounded-lg p-6 max-w-3xl mx-auto text-center">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              {t('pricing.needDifferentAccount') || 'Need a different account type?'}
            </h3>
            <p className="text-gray-600 mb-4">
              {t('pricing.accountTypeDescription') || 'You can switch between Personal and Business plans during signup, or contact us for help.'}
            </p>
            <button
              onClick={() => navigate('/demo')}
              className="px-6 py-3 bg-[#253985] text-white rounded-lg hover:bg-[#1a2c6d] transition-colors"
            >
              {t('pricing.signUpNow') || 'Sign up now'}
            </button>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Pricing;